import type { MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react/macro';
import { ArrowLeft, ArrowRight, Check, type LucideIcon } from 'lucide-react';
import { useState, type ComponentType, type MouseEvent, type ReactNode } from 'react';

import type { ApiError } from '@/shared/http';
import { useIsMobile } from '@/shared/mobile';
import { Button } from '@/shared/ui/button';
import { cn } from '@/shared/ui/cn';
import { Drawer, DrawerBody, DrawerContent, DrawerDescription, DrawerFooter, DrawerHeader, DrawerTitle } from '@/shared/ui/drawer';
import { Sheet, SheetBody, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/shared/ui/sheet';

import type { StepUpConfirm } from './step-up';
import { StepUpForm } from './step-up-form';

/** As etapas do Sheet único de transação (W11, discovery/13 §3.8): o mini-trilho "Dados · Revisão · Código". */
export type TransactionStep = 'data' | 'review' | 'code';

export const TRANSACTION_STEPS: readonly TransactionStep[] = ['data', 'review', 'code'];

const STEP_TITLE = { data: msg`Dados`, review: msg`Revisão`, code: msg`Código` } as const;

/** Uma linha da revisão: rótulo e valor, como a prévia do que vai no fio. */
export interface ReviewRow {
  readonly id: string;
  readonly label: string;
  readonly value: ReactNode;
}

/** O principal do rodapé nos dados e na revisão. */
export interface TransactionPrimary {
  readonly label: string;
  /** Envia o `<form id>` do corpo; sem ele, chama `onSelect`. */
  readonly form?: string;
  readonly onSelect?: () => void;
  readonly disabled?: boolean;
  readonly loading?: boolean;
  readonly icon?: LucideIcon;
}

export interface TransactionSheetProps<R> {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly title: string;
  readonly icon?: LucideIcon;
  readonly destructive?: boolean;
  /** As etapas que existem, na ordem do trilho (padrão: as três). Com uma só, o trilho some. */
  readonly steps?: readonly TransactionStep[];
  /** A etapa à vista. */
  readonly step: TransactionStep;
  /** A etapa que o trilho marca, quando o conteúdo dos dados já é a revisão (o Resumo do boleto). */
  readonly trailStep?: TransactionStep;
  /** O que vai acontecer (a prévia, U24): nos dados, no cabeçalho; na revisão e no código, no cartão do corpo. */
  readonly description?: ReactNode;
  /** Conteúdo dos dados (o formulário) ou da revisão (as linhas, abaixo da prévia). */
  readonly children?: ReactNode;
  /** Aviso no topo do corpo, nos dados e na revisão (erro da API, motivo do bloqueio). */
  readonly notice?: ReactNode;
  readonly primary?: TransactionPrimary;
  /** "Voltar" dos dados e da revisão. Ausente, o rodapé mostra "Cancelar", que fecha. */
  readonly onBack?: () => void;
  /** A operação sensível: com a prova depois do `StartAuth` (F2a) ou com a autorização do step-up novo (`grantConfirm`). */
  readonly confirm: StepUpConfirm<R>;
  /** Nomes próprios das etapas no trilho ("Alterações", "Origem", "Prévia"); padrão "Dados · Revisão · Código". */
  readonly stepTitles?: Partial<Record<TransactionStep, MessageDescriptor>>;
  readonly onDone: (result: R) => void;
  /** "Voltar" do código. Ausente, "Cancelar" fecha. */
  readonly onCodeBack?: () => void;
  /** Rótulo do confirmar do código ("Confirmar", "Sim, aprovar"). */
  readonly submitLabel: string;
  readonly onOperationError?: (error: ApiError) => boolean;
  /** Largura na web: `md` (≈ 612 px) para formulário, `sm` (≈ 500 px) só para revisão e código. */
  readonly size?: 'sm' | 'md';
  /** `data-slot` do painel (os testes e a casca procuram por ele). */
  readonly slot?: string;
  /** Envio fora do código em andamento (consulta, dados): trava fechar. */
  readonly busy?: boolean;
}

type Slot = ComponentType<{ readonly className?: string; readonly children: ReactNode }>;

/** O mini-trilho: concluída com ✓, atual em destaque e lida pelo leitor de tela (`aria-current="step"`). */
export function TransactionTrail({
  steps,
  current,
  titles,
}: {
  readonly steps: readonly TransactionStep[];
  readonly current: TransactionStep;
  readonly titles?: Partial<Record<TransactionStep, MessageDescriptor>> | undefined;
}) {
  const { i18n, t } = useLingui();
  const index = steps.indexOf(current);
  return (
    <ol aria-label={t`Etapas`} data-slot="transaction-trail" className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs">
      {steps.map((step, position) => {
        const state = position === index ? 'current' : position < index ? 'done' : 'pending';
        return (
          <li key={step} className="flex items-center gap-1.5" {...(state === 'current' ? { 'aria-current': 'step' as const } : {})}>
            {position > 0 ? (
              <span aria-hidden className="text-muted-foreground">
                ·
              </span>
            ) : null}
            <span className={cn(state === 'current' ? 'font-semibold text-accent-text' : state === 'done' ? 'text-foreground' : 'text-muted-foreground')}>
              {state === 'done' ? <Check aria-hidden className="mr-0.5 inline size-3" strokeWidth={3} /> : null}
              {i18n._(titles?.[step] ?? STEP_TITLE[step])}
            </span>
            <span className="sr-only">{state === 'done' ? t`concluído` : state === 'current' ? t`atual` : t`não iniciado`}</span>
          </li>
        );
      })}
    </ol>
  );
}

/** As linhas da revisão (o `dl` do Lume, como a `DescriptionList compact`). */
export function ReviewRows({ rows }: { readonly rows: readonly ReviewRow[] }) {
  if (rows.length === 0) return null;
  return (
    <dl data-slot="transaction-review" className="grid grid-cols-1 gap-x-6 gap-y-3 @min-[28rem]/review:grid-cols-2">
      {rows.map((row) => (
        <div key={row.id} className="flex min-w-0 flex-col gap-0.5">
          <dt className="text-xs text-muted-foreground">{row.label}</dt>
          <dd className="min-w-0 text-sm font-medium break-words text-foreground">{row.value}</dd>
        </div>
      ))}
    </dl>
  );
}

function MobileFooter({ className, children }: { readonly className?: string; readonly children: ReactNode }) {
  return <DrawerFooter className={cn('flex-row border-t border-border pt-3 [&>*]:flex-1', className)}>{children}</DrawerFooter>;
}

/**
 * O Sheet único de transação com MFA (W11): dados, revisão e código no mesmo painel, à direita na web (o
 * `ConfirmMfaFormDrawer` do legado) e em tela cheia no iPhone, com o mini-trilho no topo. Voltar é sempre
 * "Voltar"; na primeira etapa, "Cancelar" fecha. O principal fica no rodapé; o corpo não tem botão de navegação.
 */
export function TransactionSheet<R>(props: TransactionSheetProps<R>) {
  const { t } = useLingui();
  const isMobile = useIsMobile();
  const [codeBusy, setCodeBusy] = useState(false);
  const busy = codeBusy || props.busy === true;
  const steps = props.steps ?? TRANSACTION_STEPS;
  const trailStep = props.step === 'code' ? 'code' : (props.trailStep ?? props.step);
  const Icon = props.icon;
  const change = (next: boolean) => {
    if (!busy) props.onOpenChange(next);
  };
  const close = () => {
    if (!busy) props.onOpenChange(false);
  };

  const Title = isMobile ? DrawerTitle : SheetTitle;
  const Description = isMobile ? DrawerDescription : SheetDescription;
  const Body: Slot = isMobile ? DrawerBody : SheetBody;
  const Footer: Slot = isMobile ? MobileFooter : SheetFooter;
  const Header = isMobile ? DrawerHeader : SheetHeader;
  const inHeader = props.step === 'data';
  const hasDescription = props.description !== undefined && props.description !== null && props.description !== '';

  // Revisão e código: a prévia num cartão, no começo do corpo; ela é a descrição acessível do painel.
  const reviewCard = hasDescription ? (
    <Description asChild>
      <div data-slot="transaction-summary" className="rounded-card border border-border bg-raise px-4 py-3 text-sm text-foreground">
        {props.description}
      </div>
    </Description>
  ) : null;

  const header = (
    <Header className="flex-row items-start gap-3.5">
      {Icon ? (
        <span
          aria-hidden
          className={cn('inline-flex size-10 shrink-0 items-center justify-center rounded-[10px]', props.destructive ? 'bg-err-wash text-err' : 'bg-accent-wash text-accent-text')}
        >
          <Icon className="size-5" />
        </span>
      ) : null}
      <div className="flex min-w-0 flex-col gap-1.5">
        <Title>{props.title}</Title>
        {inHeader && hasDescription ? <Description>{props.description}</Description> : null}
        {steps.length > 1 ? <TransactionTrail steps={steps} current={trailStep} titles={props.stepTitles} /> : null}
      </div>
    </Header>
  );

  const primary = props.primary;
  const PrimaryIcon = primary?.icon ?? ArrowRight;
  const content =
    props.step === 'code' ? (
      <StepUpForm<R>
        confirm={props.confirm}
        onDone={props.onDone}
        onBack={props.onCodeBack ?? close}
        backLabel={props.onCodeBack ? t`Voltar` : t`Cancelar`}
        submitLabel={props.submitLabel}
        onBusyChange={setCodeBusy}
        {...(props.onOperationError ? { onOperationError: props.onOperationError } : {})}
        intro={reviewCard}
        Body={Body}
        Footer={Footer}
        {...(props.destructive ? { destructive: true } : {})}
      />
    ) : (
      <>
        <Body className="flex flex-col gap-4 @container/review">
          {props.notice}
          {props.step === 'review' ? reviewCard : null}
          {props.children}
        </Body>
        <Footer>
          <Button type="button" variant="secondary" disabled={busy} onClick={props.onBack ?? close}>
            {props.onBack ? <ArrowLeft aria-hidden /> : null}
            {props.onBack ? t`Voltar` : t`Cancelar`}
          </Button>
          {primary ? (
            <Button
              type={primary.form ? 'submit' : 'button'}
              {...(primary.form ? { form: primary.form } : {})}
              loading={primary.loading === true || props.busy === true}
              disabled={primary.disabled === true}
              {...(primary.onSelect && !primary.form
                ? {
                    onClick: (event: MouseEvent<HTMLButtonElement>) => {
                      // O passo interno pode trocar o principal por "enviar" no mesmo clique: o envio não pode pegar carona.
                      event.preventDefault();
                      primary.onSelect?.();
                    },
                  }
                : {})}
            >
              <PrimaryIcon aria-hidden />
              {primary.label}
            </Button>
          ) : null}
        </Footer>
      </>
    );

  // Sem prévia nenhuma, o painel não aponta para uma descrição que não existe.
  const describedBy = hasDescription ? {} : { 'aria-describedby': undefined };
  const slot = props.slot ?? 'transaction-sheet';

  if (isMobile) {
    return (
      <Drawer open={props.open} onOpenChange={change} dismissible={!busy} repositionInputs={false}>
        <DrawerContent data-slot={slot} data-step={props.step} className="h-[100dvh] max-h-[100dvh] rounded-none" {...describedBy}>
          {header}
          {content}
        </DrawerContent>
      </Drawer>
    );
  }
  return (
    <Sheet open={props.open} onOpenChange={change}>
      <SheetContent
        data-slot={slot}
        data-step={props.step}
        size={props.size ?? (steps.includes('data') ? 'md' : 'sm')}
        showClose={!busy}
        onInteractOutside={(event) => busy && event.preventDefault()}
        onEscapeKeyDown={(event) => busy && event.preventDefault()}
        {...describedBy}
      >
        {header}
        {content}
      </SheetContent>
    </Sheet>
  );
}
